import React, { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Button,
  Container,
  Dropdown,
  Nav,
  Navbar,
} from 'react-bootstrap';
import { FiMoon, FiSun } from 'react-icons/fi';
import { resolveOperationsForPath } from '../../auth/access';
import { useAuth } from '../../auth/AuthContext';
import { useTheme } from '../../theme/ThemeContext';

interface MobileNavItem {
  label: string;
  path: string;
}

interface MobileNavSection {
  id: string;
  label: string;
  items: MobileNavItem[];
}

interface AppNavbarProps {
  mobileNavSections?: MobileNavSection[];
}

export const AppNavbar: React.FC<AppNavbarProps> = ({ mobileNavSections }) => {
  const { user, logout, isAuthorized } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [expanded, setExpanded] = useState(false);

  const canAccessDashboard = isAuthorized({
    operations: resolveOperationsForPath('/admin'),
  });
  const initials = useMemo(() => {
    if (!user?.name) return '??';
    const parts = user.name.trim().split(/\s+/);
    const chars =
      parts.length === 1
        ? parts[0]
        : `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`;
    return chars.toUpperCase().slice(0, 2);
  }, [user?.name]);
  const userBadge = useMemo(() => {
    if (!user) return '';
    if (user.roles.length > 0) {
      return user.roles[0];
    }
    if (user.operations.length > 0) {
      return user.operations[0];
    }
    return 'Usuario';
  }, [user]);

  return (
    <Navbar
      bg={theme === 'dark' ? 'dark' : 'light'}
      variant={theme === 'dark' ? 'dark' : 'light'}
      expand="lg"
      expanded={expanded}
      onToggle={(value) => setExpanded((value as boolean) ?? false)}
      className="shadow-sm"
      data-bs-theme={theme}
    >
      <Container fluid className="px-4">
        <Navbar.Brand as={NavLink} to={canAccessDashboard ? "/admin" : "/"}>
          <img
            src="/tracewood-preto.png"
            alt="Tracewood"
            className="app-navbar__logo"
          />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="primary-navbar" />
        <Navbar.Collapse id="primary-navbar">
          <Nav className="me-auto">
            {canAccessDashboard && (
              <Nav.Link
                as={NavLink}
                to="/admin"
                onClick={() => setExpanded(false)}
                end
              >
                Dashboard
              </Nav.Link>
            )}
            {mobileNavSections && (
              <div className="d-lg-none w-100 mt-3">
                {mobileNavSections.map((section) => (
                  <div key={section.id} className="mb-2">
                    <div className="text-uppercase text-muted small fw-semibold mb-1">
                      {section.label}
                    </div>
                    <Nav className="flex-column">
                      {section.items.map((item) => (
                        <Nav.Link
                          key={item.path}
                          as={NavLink}
                          to={item.path}
                          onClick={() => setExpanded(false)}
                          className="ps-3"
                        >
                          {item.label}
                        </Nav.Link>
                      ))}
                    </Nav>
                  </div>
                ))}
              </div>
            )}
          </Nav>
          <Nav className="align-items-center gap-2">
            <Button
              variant="link"
              className="app-navbar__theme-toggle nav-link px-2 py-2 w-auto"
              onClick={toggleTheme}
            >
              <span className="app-navbar__theme-icon" aria-hidden="true">
                {theme === 'dark' ? <FiSun size={18} /> : <FiMoon size={18} />}
              </span>
              <span className="visually-hidden">
                Alternar para modo {theme === 'dark' ? 'claro' : 'escuro'}
              </span>
            </Button>
            {user ? (
              <Dropdown as={Nav.Item} align="end">
                  <Dropdown.Toggle
                    as={Nav.Link}
                    className="app-navbar__user-toggle d-flex align-items-center gap-2 px-2 py-2 w-auto"
                  >
                  <span className="app-navbar__avatar" aria-hidden="true">
                    {initials}
                  </span>
                    <span className="app-navbar__user">
                    <span className="app-navbar__user-name">{user.name}</span>
                    <span className="app-navbar__user-role">{userBadge}</span>
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100 w-lg-auto">
                  <Dropdown.Item disabled>{user.email}</Dropdown.Item>
                  <Dropdown.Item
                    as={NavLink}
                    to="/admin/perfil/mensagens"
                    onClick={() => setExpanded(false)}
                  >
                    Mensagens
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => logout()}>
                    Sair
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <Nav.Link
                as={NavLink}
                to="/"
                onClick={() => setExpanded(false)}
                className="nav-link-button"
              >
                Entrar
              </Nav.Link>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};
